import { OnboardingPeriodDto } from '@zorro/clients';
import { APP_ROUTES } from '@zorro/common-utils';
import { StrictExtract } from '@zorro/types';
import { $enum } from 'ts-enum-util';

type ManualSubmissionRoutes = StrictExtract<
  typeof APP_ROUTES,
  | 'OPS_EMPLOYEE_PLANS_TAB'
  | 'OPS_EMPLOYEE_PAGE'
  | 'DON_EMPLOYEE_PLANS_TAB'
  | 'DON_EMPLOYEE_PAGE'
>;

export type ManualSubmissionStepSubmit = () => Promise<{
  navigate: boolean;
}>;

export enum ManualSubmissionStepType {
  HOUSEHOLD_INFORMATION = 'HOUSEHOLD_INFORMATION',
  MEDICAL_PLAN = 'MEDICAL_PLAN',
  DENTAL = 'DENTAL',
  VISION = 'VISION',
  REVIEW = 'REVIEW',
}

export enum ManualSubmissionStepChangeDirection {
  FORWARD = 'FORWARD',
  BACK = 'BACK',
  SKIP = 'SKIP',
}

export const getManualSubmissionRoute = (isDon: boolean) => {
  return isDon ? 'DON_EMPLOYEE_PLANS_TAB' : 'OPS_EMPLOYEE_PLANS_TAB';
};

export const getManualSubmissionHidden = (
  step: ManualSubmissionStepType,
  onboardingPeriod?: OnboardingPeriodDto
) => {
  return $enum.mapValue(step).with<boolean>({
    [ManualSubmissionStepType.DENTAL]:
      !onboardingPeriod?.enrollmentIsDentalEnabled,
    [ManualSubmissionStepType.VISION]:
      !onboardingPeriod?.enrollmentIsVisionEnabled,
    [ManualSubmissionStepType.HOUSEHOLD_INFORMATION]: false,
    [ManualSubmissionStepType.MEDICAL_PLAN]: false,
    [ManualSubmissionStepType.REVIEW]: false,
  });
};

export const getManualSubmissionStepConfig = (
  step: ManualSubmissionStepType,
  isDon: boolean,
  onboardingPeriod?: OnboardingPeriodDto
) =>
  $enum.mapValue(step).with<{
    nextRoute: ManualSubmissionRoutes;
    prevStep?: ManualSubmissionStepType;
    nextStep: ManualSubmissionStepType;
    stepIndex: number;
    label: string;
    isHidden?: boolean;
    withSkipButton?: boolean;
  }>({
    [ManualSubmissionStepType.HOUSEHOLD_INFORMATION]: {
      nextRoute: getManualSubmissionRoute(isDon),
      nextStep: ManualSubmissionStepType.MEDICAL_PLAN,
      label: 'Household information',
      stepIndex: 0,
    },
    [ManualSubmissionStepType.MEDICAL_PLAN]: {
      nextRoute: getManualSubmissionRoute(isDon),
      prevStep: ManualSubmissionStepType.HOUSEHOLD_INFORMATION,
      nextStep: onboardingPeriod?.enrollmentIsDentalEnabled
        ? ManualSubmissionStepType.DENTAL
        : onboardingPeriod?.enrollmentIsVisionEnabled
          ? ManualSubmissionStepType.VISION
          : ManualSubmissionStepType.REVIEW,
      label: 'Medical plan',
      stepIndex: 1,
    },
    [ManualSubmissionStepType.DENTAL]: {
      nextRoute: getManualSubmissionRoute(isDon),
      prevStep: ManualSubmissionStepType.MEDICAL_PLAN,
      nextStep: onboardingPeriod?.enrollmentIsVisionEnabled
        ? ManualSubmissionStepType.VISION
        : ManualSubmissionStepType.REVIEW,
      label: 'Dental plan',
      stepIndex: 1 + (onboardingPeriod?.enrollmentIsDentalEnabled ? 1 : 0),
      isHidden: !onboardingPeriod?.enrollmentIsDentalEnabled,
      withSkipButton: true,
    },
    [ManualSubmissionStepType.VISION]: {
      nextRoute: getManualSubmissionRoute(isDon),
      prevStep: onboardingPeriod?.enrollmentIsDentalEnabled
        ? ManualSubmissionStepType.DENTAL
        : ManualSubmissionStepType.MEDICAL_PLAN,
      nextStep: ManualSubmissionStepType.REVIEW,
      label: 'Vision plan',
      stepIndex:
        1 +
        (onboardingPeriod?.enrollmentIsDentalEnabled ? 1 : 0) +
        (onboardingPeriod?.enrollmentIsVisionEnabled ? 1 : 0),
      isHidden: !onboardingPeriod?.enrollmentIsVisionEnabled,
      withSkipButton: true,
    },
    [ManualSubmissionStepType.REVIEW]: {
      nextRoute: isDon ? 'DON_EMPLOYEE_PAGE' : 'OPS_EMPLOYEE_PAGE',
      prevStep: onboardingPeriod?.enrollmentIsVisionEnabled
        ? ManualSubmissionStepType.VISION
        : onboardingPeriod?.enrollmentIsDentalEnabled
          ? ManualSubmissionStepType.DENTAL
          : ManualSubmissionStepType.MEDICAL_PLAN,
      nextStep: ManualSubmissionStepType.HOUSEHOLD_INFORMATION,
      label: 'Review',
      stepIndex:
        2 +
        (onboardingPeriod?.enrollmentIsDentalEnabled ? 1 : 0) +
        (onboardingPeriod?.enrollmentIsVisionEnabled ? 1 : 0),
    },
  });
